import type {
  AckPayloads,
  AgentUsingBase,
  AudienceInput,
  GroupRef,
  KnowledgeAllowance,
  KnowledgeBase,
  KnowledgeBaseView,
  LendingAgent,
  LendingRequest,
  LiveAudience,
  OwnerView,
  ShareOutcome
} from '@ralph/contracts'
import { ListingStateSchema, splitChange } from '@ralph/contracts'
import type { ReviewLine } from '@/components/groups/audience-picker.ts'
import { foldForSearch } from '@/i18n/format.ts'
import type { I18n } from '@/i18n/index.ts'
import type { IndexHealth } from './knowledge-console.ts'

/**
 * The pure half of the knowledge pages (PRD-26): given a base and who is looking, what is true
 * and what should the screen say. Each function exists to stop one specific lie, and each is
 * pinned by `knowledge-base.spec.ts`:
 *
 *  - a list row that says a base exists but not why this reader has it, so "shared with Legal"
 *    and "yours" look alike (`whyLine`);
 *  - a group-owned base that reads as though the whole group can use it — being in the group
 *    that OWNS something gives nothing on its own (D-9), only sharing does (`whyLine`);
 *  - a base whose documents failed wearing the same mark as one that answers (`baseStanding`);
 *  - limits discovered at the refusal rather than read before it (`ownerAllowance`, D-41);
 *  - a decision about letting an agent answer for everyone it reaches taken on a button label
 *    instead of on the facts that make it consequential (`lendingDisclosure`, D-16).
 *
 * Every function that speaks takes the words as a parameter and reads them inside its own body
 * (PRD-27 LOC-012). A module-scope `const t = …` here would freeze the catalogue branch at
 * import and then render the previous language after a locale switch, with no type error, no
 * runtime error and no failing test (F-3) — which is exactly what this file used to do.
 */

/**
 * What these functions need from the reader's locale: the words, and the three formatters the
 * sentences interpolate. `useI18n()` satisfies it whole, so a component passes the hook's value
 * and nothing threads a locale by hand; a test passes a fixture built from the real `Intl`.
 */
export type Fmt = Pick<I18n, 'locale' | 't' | 'formatCount' | 'formatList'>

// ---------------------------------------------------------------------------
// Owners
// ---------------------------------------------------------------------------

/** "you", a person's name, or a group's name — how an owner is said in a sentence. */
export const ownerName = (fmt: Fmt, owner: OwnerView, viewerId: string | null): string =>
  owner.kind === 'group' ? owner.group.name : owner.id === viewerId ? fmt.t.knowledge.base.you : owner.name

export const ownerRef = (owner: OwnerView): { kind: 'user' | 'group'; id: string } =>
  owner.kind === 'group' ? { kind: 'group', id: owner.group.id } : { kind: 'user', id: owner.id }

/** The picker's "only its maintainers", in the words that name them (D-37). */
export const privateLabelFor = (fmt: Fmt, owner: OwnerView, viewerId: string | null): string =>
  owner.kind === 'group'
    ? fmt.t.groups.privateLabel.groupManagers(owner.group.name)
    : owner.id === viewerId
      ? fmt.t.groups.privateLabel.me
      : fmt.t.knowledge.access.onlyPerson(owner.name)

// ---------------------------------------------------------------------------
// Why this reader can use it
// ---------------------------------------------------------------------------

export type WhyMark = 'private' | 'group' | 'everyone' | 'administrator'

/**
 * The line under a base's name: why this reader has it, and — for its maintainers — who else
 * does. The group involved is returned so the row can draw its avatar beside its name (GRP-102).
 *
 *   "Only you" · "Yours · shared with Legal" · "Owned by Finance EMEA · only its managers"
 *   "Shared with Legal · includes you" · "Shared with everyone"
 *   "Owned by Ana Souza · only its owner · you can open it as an administrator"
 */
export type WhyLine = { text: string; group: GroupRef | null; mark: WhyMark }

const MAX_NAMED = 2

const namedGroups = (groups: readonly GroupRef[]): { names: string[]; more: number } => ({
  names: groups.slice(0, MAX_NAMED).map((group) => group.name),
  more: Math.max(0, groups.length - MAX_NAMED)
})

/** Who can use it, as a maintainer's second clause: never an id, never a bare count. */
const whoElse = (fmt: Fmt, base: Pick<KnowledgeBase, 'owner' | 'access'>): string => {
  const t = fmt.t.knowledge
  if (base.access.kind === 'everyone') return t.why.sharedWithEveryone
  if (base.access.kind === 'groups' && base.access.groups.length > 0) {
    const { names, more } = namedGroups(base.access.groups)
    return t.why.sharedWith(fmt, names, more)
  }
  return base.owner.kind === 'group' ? t.why.onlyItsManagers : t.why.onlyItsOwner
}

export const whyLine = (fmt: Fmt, base: Pick<KnowledgeBase, 'owner' | 'access' | 'viewer'>): WhyLine => {
  const t = fmt.t.knowledge
  const { viewer } = base
  const firstShared = base.access.kind === 'groups' ? (base.access.groups[0] ?? null) : null

  if (viewer.via === 'maintainer') {
    if (base.owner.kind === 'group') {
      return {
        text: `${t.why.ownedBy(base.owner.group.name)} · ${whoElse(fmt, base)}`,
        group: base.owner.group,
        mark: 'group'
      }
    }
    if (base.access.kind === 'everyone')
      return { text: `${t.why.yours} · ${whoElse(fmt, base)}`, group: null, mark: 'everyone' }
    if (base.access.kind === 'groups' && firstShared) {
      return { text: `${t.why.yours} · ${whoElse(fmt, base)}`, group: firstShared, mark: 'group' }
    }
    return { text: t.why.onlyYou, group: null, mark: 'private' }
  }

  if (viewer.via === 'group' && viewer.viaGroups.length > 0) {
    const { names, more } = namedGroups(viewer.viaGroups)
    return {
      text: `${t.why.sharedWithYou(fmt, names, more)} · ${t.why.includesYou(fmt, viewer.viaGroups.length)}`,
      group: viewer.viaGroups[0] ?? null,
      mark: 'group'
    }
  }

  if (viewer.via === 'everyone') return { text: t.why.everyone, group: null, mark: 'everyone' }

  // An administrator looking at something that is not theirs, or shared with them: whose it is
  // and who has it, then the only reason they can see it at all.
  const owner = t.why.ownedBy(base.owner.kind === 'group' ? base.owner.group.name : base.owner.name)
  const tail = viewer.via === 'administrator' ? t.why.administrator : t.why.notForYou
  return {
    text: `${owner} · ${whoElse(fmt, base)} · ${tail}`,
    group: base.owner.kind === 'group' ? base.owner.group : firstShared,
    mark: viewer.via === 'administrator' ? 'administrator' : 'private'
  }
}

/**
 * The access line on a base's own card, under "Owned by …": for its maintainers, who can use it
 * (the owner is already on the line above); for everyone else, why they can.
 */
export const accessLine = (
  fmt: Fmt,
  base: Pick<KnowledgeBase, 'owner' | 'access' | 'viewer'>,
  viewerId: string | null
): WhyLine => {
  const t = fmt.t.knowledge
  if (!base.viewer.canMaintain) return whyLine(fmt, base)
  if (base.access.kind === 'everyone') return { text: t.why.everyone, group: null, mark: 'everyone' }
  if (base.access.kind === 'groups' && base.access.groups.length > 0) {
    const { names, more } = namedGroups(base.access.groups)
    return { text: t.why.sharedWithYou(fmt, names, more), group: base.access.groups[0] ?? null, mark: 'group' }
  }
  const who =
    base.owner.kind === 'group'
      ? t.agents.groupManagers(base.owner.group.name)
      : base.owner.id === viewerId
        ? t.base.you
        : base.owner.name
  return { text: t.why.onlyCanUse(who), group: null, mark: 'private' }
}

/**
 * Whether "Move…" has anywhere to go (D-32): an administrator can move anything anywhere; anybody
 * else moves a base only to a group they manage — never to themselves, which the server refuses.
 * A base whose maintainer manages no group other than its owner has nowhere to go, and is not
 * offered a dialog whose only choice would be refused.
 */
export const hasMoveDestination = (
  owner: OwnerView,
  viewer: { id: string | null; isAdmin: boolean; managedGroups: readonly GroupRef[] }
): boolean => {
  if (viewer.isAdmin) return true
  return viewer.managedGroups.some((group) => !(owner.kind === 'group' && owner.group.id === group.id))
}

/** Opened only because the reader is an administrator: the read is recorded (D-18). */
export const opensAsAdministrator = (base: Pick<KnowledgeBase, 'viewer'>): boolean =>
  base.viewer.via === 'administrator' || (!base.viewer.canUse && base.viewer.canMaintain)

// ---------------------------------------------------------------------------
// Standing: what the list row's mark says
// ---------------------------------------------------------------------------

/**
 * What a base is doing for the agents that use it, in the six words a mark can carry.
 *
 *   `empty`     — nothing has ever been put in it. Not stale: a base that was never filled has
 *                 not gone out of date, it has not started.
 *   `reading`   — documents are still being read, and nothing has failed.
 *   `silent`    — it holds documents and none of them answer.
 *   `attention` — some answer and some cannot, or a connected folder needs a person.
 *   `stale`     — everything answers, and nothing has been added within its freshness window.
 */
export type BaseStanding = 'empty' | 'reading' | 'silent' | 'attention' | 'stale' | 'ready'

type Census = Pick<
  KnowledgeBase,
  'documentCount' | 'answeringCount' | 'status' | 'stale' | 'connectedFolders' | 'lastIngestedAt'
>

export const baseStanding = (base: Census): BaseStanding => {
  if (base.connectedFolders.needsPerson > 0) return 'attention'
  if (base.documentCount === 0) return base.connectedFolders.total > 0 ? 'reading' : 'empty'
  if (base.status === 'reading' && base.answeringCount < base.documentCount) return 'reading'
  if (base.answeringCount === 0) return 'silent'
  if (base.answeringCount < base.documentCount) return 'attention'
  return base.stale ? 'stale' : 'ready'
}

/** The row's census line: the most urgent fact first, then what it holds, then its folders. */
export const censusLine = (fmt: Fmt, base: Census, when: (iso: string) => string): string => {
  const t = fmt.t.knowledge
  const folders = base.connectedFolders
  const parts: string[] = []
  if (folders.needsPerson > 0) parts.push(t.row.folderNeedsPerson(fmt, folders.needsPerson))

  if (base.documentCount === 0) {
    if (folders.needsPerson === 0) parts.push(folders.total > 0 ? t.row.loadingFromFolder : t.row.noDocuments)
  } else if (base.status === 'reading' && base.answeringCount < base.documentCount) {
    parts.push(t.row.readingRest(fmt, base.answeringCount, base.documentCount))
  } else if (base.answeringCount === 0) {
    parts.push(t.row.nothingAnswering(fmt, base.documentCount))
  } else if (base.answeringCount < base.documentCount) {
    parts.push(t.row.someAnswering(fmt, base.answeringCount, base.documentCount))
  } else {
    parts.push(t.row.allAnswering(fmt, base.documentCount))
    if (base.stale && base.lastIngestedAt) parts.push(t.row.notUpdated(when(base.lastIngestedAt)))
  }

  if (folders.total > 0 && folders.needsPerson === 0) parts.push(t.row.folders(fmt, folders.total))
  // A middot-separated run of clauses, not a conjunction: these are readouts stacked in a row,
  // and `Intl.ListFormat` would turn them into a sentence they are not.
  return parts.join(' · ')
}

/**
 * The one sentence at the top of the documents pane.
 *
 * It leads with what works, because that is the number a maintainer is accountable for, and it
 * names the exception in the same breath rather than leaving it to a badge further down. When
 * everything is fine it says so in the fewest words that are still a claim — silence would be
 * indistinguishable from a panel that had not loaded.
 *
 * It lives here rather than beside `indexHealth` because it SPEAKS, and `knowledge-console.ts`
 * is deliberately free of locale: it counts, and every word about those counts is catalogue copy
 * (LOC-010). The composition itself — what comes first, how the problems are joined, where the
 * stop goes — is the catalogue's too (`health.summary`), because none of it is the same in two
 * languages.
 */
export const healthSummary = (fmt: Fmt, health: IndexHealth): string => {
  const t = fmt.t.knowledge.health
  if (health.total === 0) return t.nothingYet

  const problems: string[] = []
  if (health.unreadable > 0) problems.push(t.problem.unreadable(fmt, health.unreadable))
  if (health.quarantined > 0) problems.push(t.problem.quarantined(fmt, health.quarantined))
  if (health.silent > 0) problems.push(t.problem.silent(fmt, health.silent))
  if (health.working > 0) problems.push(t.problem.working(fmt, health.working))

  if (problems.length === 0) return t.allAnswering(fmt, health.total, health.passages)

  // `health.total` is at least two here: a single document either answers (no problem) or is
  // the problem, and the branch below says so without a ratio.
  const lead = health.answering === 0 ? t.nothingCited : t.ratio(fmt, health.answering, health.total, health.passages)
  return t.summary(fmt, lead, problems)
}

// ---------------------------------------------------------------------------
// The list: which view, and what matches
// ---------------------------------------------------------------------------

export const parseView = (raw: string | null, isAdmin: boolean): KnowledgeBaseView | null =>
  raw === 'maintain' || raw === 'use' || (raw === 'all' && isAdmin) ? raw : null

/**
 * Which list opens when the address names none: what this person maintains, unless they
 * maintain nothing — a reader arriving on an empty "You maintain" has been shown the wrong page.
 */
export const defaultView = (maintained: number | null): KnowledgeBaseView =>
  maintained === null || maintained > 0 ? 'maintain' : 'use'

export const matchesQuery = (base: Pick<KnowledgeBase, 'name' | 'description'>, query: string): boolean => {
  // Folded on both sides (LOC-023), so "politicas" finds "Políticas de viagem". Before this a
  // Brazilian who typed the name of their own knowledge base without its accents got an empty
  // list and no hint that the search had been literal.
  const term = foldForSearch(query.trim())
  return term === '' || foldForSearch(`${base.name} ${base.description}`).includes(term)
}

/**
 * The addresses that predate PRD-26 — `/knowledge?source=<id>&view=folders`, which the command
 * palette and the connected-folders sign-in still send — mapped onto a base's own address, with
 * the sign-in's outcome carried along so the folders tab can resume or explain.
 */
export const legacyTarget = (params: URLSearchParams): string | null => {
  const source = params.get('source')
  if (!source) return null
  const next = new URLSearchParams()
  const view = params.get('view')
  if (view === 'folders') next.set('tab', 'folders')
  else if (view === 'retrieval') next.set('tab', 'search')
  for (const key of ['connected', 'connectFailed', 'doc', 'folder']) {
    const value = params.get(key)
    if (value) next.set(key, value)
  }
  const query = next.toString()
  return `/knowledge/${encodeURIComponent(source)}${query === '' ? '' : `?${query}`}`
}

// ---------------------------------------------------------------------------
// Limits (D-41): read before the refusal, not discovered at it
// ---------------------------------------------------------------------------

export type OwnerAllowance =
  | { kind: 'unlimited' }
  | {
      kind: 'known'
      owner: 'you' | 'group'
      groupName: string | null
      bases: number
      used: number
      documentsPerBase: number
      storagePerBaseBytes: number
      full: boolean
    }
  | { kind: 'unknown' }

/** What the chosen owner may still hold, from the caller's own allowance (the only one it has). */
export const ownerAllowance = (
  allowance: KnowledgeAllowance | null,
  owner: { kind: 'user'; id: string } | { kind: 'group'; id: string } | null,
  viewerId: string | null
): OwnerAllowance => {
  if (!allowance || !owner) return { kind: 'unknown' }
  if (owner.kind === 'user') {
    if (owner.id !== viewerId) return { kind: 'unknown' }
    const personal = allowance.personal
    if (personal === null) return { kind: 'unlimited' }
    return {
      kind: 'known',
      owner: 'you',
      groupName: null,
      bases: personal.bases,
      used: personal.used,
      documentsPerBase: personal.documentsPerBase,
      storagePerBaseBytes: personal.storagePerBaseBytes,
      full: personal.used >= personal.bases
    }
  }
  const entry = allowance.groups.find((candidate) => candidate.group.id === owner.id)
  if (!entry) return { kind: 'unknown' }
  return {
    kind: 'known',
    owner: 'group',
    groupName: entry.group.name,
    bases: entry.bases,
    used: entry.used,
    documentsPerBase: entry.documentsPerBase,
    storagePerBaseBytes: entry.storagePerBaseBytes,
    full: entry.used >= entry.bases
  }
}

/** The sentences under an owner choice: what it has, what each base may hold, or why it cannot. */
export const limitsSentence = (fmt: Fmt & Pick<I18n, 'formatBytes'>, limits: OwnerAllowance): string => {
  const t = fmt.t.knowledge
  if (limits.kind === 'unlimited') return t.limits.unlimited
  if (limits.kind === 'unknown') return t.limits.unknown
  if (limits.full) {
    return limits.owner === 'you'
      ? t.limits.youFull(fmt, limits.bases)
      : t.limits.groupFull(fmt, limits.groupName ?? '', limits.bases)
  }
  const holds =
    limits.owner === 'you'
      ? t.limits.you(fmt, limits.used, limits.bases)
      : t.limits.group(fmt, limits.groupName ?? '', limits.used, limits.bases)
  return t.limits.holdsAndPerBase(
    holds,
    t.limits.perBase(fmt, limits.documentsPerBase, fmt.formatBytes(limits.storagePerBaseBytes))
  )
}

/** A base that already holds more than a new owner's bases may: it moves, and takes nothing new. */
export const exceedsLimits = (
  base: Pick<KnowledgeBase, 'documentCount' | 'storageBytes'>,
  limits: OwnerAllowance
): boolean =>
  limits.kind === 'known' &&
  (base.documentCount > limits.documentsPerBase || base.storageBytes > limits.storagePerBaseBytes)

/** Room left in one base, for the documents toolbar; `null` when its owner is unlimited. */
export const baseRoom = (
  base: Pick<KnowledgeBase, 'documentCount' | 'storageBytes' | 'limits'>
): { documents: number; storage: number; full: boolean } | null => {
  if (base.limits === null) return null
  const documents = Math.min(1, base.documentCount / base.limits.documents)
  const storage = Math.min(1, base.storageBytes / base.limits.storageBytes)
  return {
    documents,
    storage,
    full: base.documentCount >= base.limits.documents || base.storageBytes >= base.limits.storageBytes
  }
}

// ---------------------------------------------------------------------------
// Who can use it: saving, and what the save did
// ---------------------------------------------------------------------------

/** The Save button says what will happen: saved, sent for review, or both (D-40). */
export const saveVerb = (line: ReviewLine): 'save' | 'send' | 'save_and_send' => {
  if (line.kind === 'everyone') return 'send'
  if (line.kind === 'groups') return line.nowAdded.length > 0 || line.nowRemoved.length > 0 ? 'save_and_send' : 'send'
  return 'save'
}

/** A change reaches people it did not reach before. */
export const widens = (live: LiveAudience | null, next: AudienceInput | LiveAudience): boolean => {
  const before: LiveAudience = live ?? { kind: 'private', groupIds: [] }
  if (next.kind === 'private' || before.kind === 'everyone') return false
  if (next.kind === 'everyone') return true
  const liveGroups = new Set(before.kind === 'groups' ? before.groupIds : [])
  return next.groupIds.some((id) => !liveGroups.has(id))
}

/**
 * When a change makes the base's connected folders ask again (D-38): `now` when what goes live at
 * Save reaches people it did not, `after_approval` when only the part that waits for an
 * administrator would — the server marks the folders when that part is applied, not at Save — and
 * `null` when nobody new is reached. Split with the server's own `splitChange` (D-40).
 */
export const foldersAskAgain = (
  live: LiveAudience | null,
  next: AudienceInput,
  viewer: { isAdmin: boolean; managedGroupIds: readonly string[] }
): 'now' | 'after_approval' | null => {
  const before: LiveAudience = live ?? { kind: 'private', groupIds: [] }
  const split = splitChange({
    actorIsAdmin: viewer.isAdmin,
    actorManagedGroupIds: viewer.managedGroupIds,
    live: before,
    requested: { kind: next.kind, groupIds: next.kind === 'groups' ? [...new Set(next.groupIds)] : [] }
  })
  if (split.now !== null && widens(before, split.now)) return 'now'
  // Whatever waits for review always reaches somebody new: everyone, or a group not yet live.
  return split.review !== null ? 'after_approval' : null
}

export type SaveResult =
  | { kind: 'nothing' }
  | { kind: 'applied' }
  | { kind: 'sent'; everyone: boolean; groups: GroupRef[] }
  | { kind: 'applied_and_sent'; everyone: boolean; groups: GroupRef[] }

/** What PUT /access did, as the sentence after Save reads it. */
export const saveResult = (outcome: ShareOutcome): SaveResult => {
  if (!outcome.applied && outcome.request === null) return { kind: 'nothing' }
  if (outcome.request === null) return { kind: 'applied' }
  const waiting = {
    everyone: outcome.request.requestedKind === 'everyone',
    groups: outcome.request.groups
  }
  return outcome.applied ? { kind: 'applied_and_sent', ...waiting } : { kind: 'sent', ...waiting }
}

// ---------------------------------------------------------------------------
// Agents that use it (D-16, D-17)
// ---------------------------------------------------------------------------

export type AgentAnswering = 'everyone' | 'users' | 'detached'

/** Whom an attached agent answers from this base for, and what the maintainer can do about it. */
export const agentAnswering = (entry: Pick<AgentUsingBase, 'attached' | 'lending'>): AgentAnswering =>
  entry.lending === null ? 'users' : entry.attached ? 'everyone' : 'detached'

/** Letting it answer for everyone is offered only where it would do something: attached, not yet allowed. */
export const canLetAnswer = (entry: Pick<AgentUsingBase, 'attached' | 'lending'>): boolean =>
  entry.attached && entry.lending === null

/**
 * The quiet line for a request opened from a notification that is no longer waiting (a link that
 * carries `?lending=`): who decided it and when, that it was withdrawn or closed and why — or, when
 * it cannot be read at all, that there is nothing left to decide. `null` while it is still waiting
 * where the maintainer can decide it, which is the highlighted row rather than a sentence.
 */
export const requestNotice = (
  fmt: Fmt,
  input: { request: LendingRequest | null; waitingHere: boolean; unreadable: boolean },
  when: (iso: string) => string
): string | null => {
  const t = fmt.t.knowledge
  if (input.waitingHere) return null
  const { request } = input
  if (!request) return input.unreadable ? t.agents.decided.gone : null
  const asker = request.requestedBy.displayName
  switch (request.status) {
    case 'pending':
      return t.agents.decided.detached(request.agent.name)
    case 'approved':
    case 'declined': {
      const by = request.decidedBy?.displayName ?? null
      const at = request.decidedAt ? when(request.decidedAt) : ''
      return request.status === 'approved'
        ? t.agents.decided.approved(asker, by, at)
        : t.agents.decided.declined(asker, by, at)
    }
    case 'withdrawn':
      return t.agents.decided.withdrawn(asker)
    case 'closed':
      return t.agents.decided.closed(
        asker,
        request.closedReason ? t.agents.closedReason[request.closedReason] : t.agents.closedReason.no_longer_eligible
      )
  }
}

/** Who maintains an agent, as the decision names them. */
export const agentMaintainers = (fmt: Fmt, owner: OwnerView): string =>
  owner.kind === 'group' ? fmt.t.knowledge.agents.groupManagers(owner.group.name) : owner.name

/**
 * The badge beside an agent that is not live, in the words the builder uses for the same state.
 *
 * `LendingAgent.state` is `z.string()` on the wire (contracts `sharing.ts:385`), not the
 * `ListingState` it actually carries, so the value is parsed back into the enum before it is
 * looked up. It used to be `map[state as ListingState] ?? state`, and that pair of mistakes
 * cancelled out until it didn't: the cast defeated the exhaustiveness the map carries, and the
 * `??` then put the raw code on a badge (F-8). An unrecognised state shows no badge at all,
 * which is what "" already means for `published`.
 *
 * The parse lives here rather than in the catalogue because it is logic, not copy (D-25): a
 * naively copied locale file must not be able to change which states this product recognises.
 */
export const agentStateLabel = (fmt: Fmt, state: string): string => {
  const parsed = ListingStateSchema.safeParse(state)
  return parsed.success ? fmt.t.knowledge.agents.state[parsed.data] : ''
}

/**
 * The decision's facts as VALUES — who, what it can do, how far it reaches, and whether it is
 * live — which is what is persisted as the record of what was agreed to (PRD-27 D-7).
 *
 * Only values: an owner's kind and name rather than "Finance's managers", a boolean rather than
 * the sentence it selects. A record holding a phrase is a record that reads back in one language
 * only, which is the defect this shape exists to remove (D-20).
 */
export const lendingAck = (
  agent: Pick<LendingAgent, 'name' | 'owner' | 'acts' | 'delegates' | 'reach' | 'state'>,
  beyondAccess: number,
  baseName: string
): AckPayloads['ack.lending'] => ({
  agent: agent.name,
  base: baseName,
  ownerKind: agent.owner.kind === 'group' ? 'group' : 'user',
  ownerName: agent.owner.kind === 'group' ? agent.owner.group.name : agent.owner.name,
  acts: agent.acts,
  delegates: agent.delegates,
  reach: agent.reach,
  beyond: beyondAccess,
  // Nobody uses an agent that is not live, whatever its maintainers could: no count to agree to.
  live: agent.state === 'published'
})

/**
 * What a maintainer reads before an agent answers from their base for everyone it reaches —
 * and, verbatim, the evidence stored with the decision (AGM-042). Five facts, because each is
 * one a maintainer has been surprised by: who is accountable for the agent, whether it can act,
 * how many people it reaches today, that the reach grows without asking, and that stopping later
 * does not take back what was already said.
 *
 * The sentences are the catalogue's and are rendered from the record's own parameters, so the
 * dialog and a replay months later in the other language run the same code (PRD-27 D-7).
 */
export const lendingDisclosure = (
  fmt: Fmt,
  agent: Pick<LendingAgent, 'name' | 'owner' | 'acts' | 'delegates' | 'reach' | 'state'>,
  beyondAccess: number,
  baseName: string
): string[] => fmt.t.knowledge.lend.disclosure(fmt, lendingAck(agent, beyondAccess, baseName))

// ---------------------------------------------------------------------------
// Uploads: what one file's answer means
// ---------------------------------------------------------------------------

/**
 * One file's outcome, in the words the upload summary uses. A file that was stored and could not
 * be read is an outcome, not a failure of the request — its row says why (PRD-24 D-5).
 */
export type UploadOutcome = 'added' | 'reading' | 'unreadable' | 'quarantined' | 'reused'

export const uploadOutcome = (result: { status: string; reused: boolean }): UploadOutcome => {
  if (result.reused) return 'reused'
  if (result.status === 'indexed') return 'added'
  if (result.status === 'failed') return 'unreadable'
  if (result.status === 'quarantined') return 'quarantined'
  return 'reading'
}

/** Stored in the base, whatever became of it: everything but a file that was already there. */
export const wasStored = (outcome: UploadOutcome | 'refused'): boolean =>
  outcome === 'added' || outcome === 'reading' || outcome === 'unreadable' || outcome === 'quarantined'

// ---------------------------------------------------------------------------
// Deleting: the typed name, as the server compares it
// ---------------------------------------------------------------------------

/** The server compares trimmed and case-insensitively; so does the button. */
export const confirmsName = (typed: string, name: string): boolean =>
  typed.trim().toLowerCase() === name.trim().toLowerCase()

/**
 * "90 days", never "2160h": the freshness window in the unit a person keeps it in.
 *
 * The UNIT is chosen by arithmetic here and the FORM by CLDR in the catalogue, never by `=== 1`:
 * Portuguese takes the singular at zero, and a window is a count like any other.
 */
export const formatWindow = (fmt: Fmt, hours: number): string => {
  const said = fmt.t.knowledge.base.window
  if (hours % 168 === 0 && hours >= 168) return said.weeks(fmt, hours / 168)
  if (hours % 24 === 0 && hours >= 24) return said.days(fmt, hours / 24)
  return said.hours(fmt, hours)
}
